import { logError } from 'App/services/faroService';
import { LOCAL_STORAGE_IDS } from 'Shared/constants';
import { ACTUserPointsResponse } from 'Shared/models/swagger/act';
import { VERUserVerificationStatus, VERVerificationResultStatusEnum } from 'Shared/models/swagger/ver';
import { analyticsTrackChatBotStart } from 'Shared/services/analytics/events/chatBotStart';
import { isMobile } from 'Shared/services/deviceService';
import { getLocalStorage, setLocalStorage } from 'Shared/services/localStorageService';
import { isInMobileApp } from 'Shared/services/mobileAppMessenger';
import { IDPUserResponseInternal } from 'Shared/services/userActivation/api/consts';

type FreshChatResponseType = {
  restoreId: string
}

type FcInitInfoType = {
  token             : string | undefined,
  host              : string | undefined,
  restoreId         : string | null,
  fullscreen        : boolean;
  firstName         : string | undefined;
  lastName          : string | undefined;
  widgetUuid?       : string;
  externalId        : number | undefined;
  current_points    : number;
  lifetime_points   : number;
  earned_this_year  : string;
  email             : string | undefined;
  customer_name     : string | undefined;
  customer_last_name: string | undefined;
  VVS               : VERVerificationResultStatusEnum | undefined;
  hideChatButton?   : boolean;
}

// Module state
let scriptLoadingPromise: Promise<void> | null = null;

/**
 * Destroy FreshChat
 * @returns void
 */
export const destroyFreshChat = () => {
  if (!window.fcWidget || typeof window.fcWidget.destroy !== 'function') {
    return;
  }

  window.fcWidget.destroy();
};

/**
 * Hide FreshChat
 * @returns void
 */
export const hideFreshChat = () => {
  if (!window.fcWidget || typeof window.fcWidget.hide !== 'function') {
    return;
  }

  window.fcWidget.hide();
};

/**
 * Lazy Load FreshChat script
 */
export const loadFreshchatScript = (): Promise<void> => {
  if (document.getElementById('Freshchat-js-sdk') && window.fcWidget) {
    return Promise.resolve();
  }

  if (scriptLoadingPromise) {
    return scriptLoadingPromise;
  }

  scriptLoadingPromise = new Promise((resolve, reject) => {
    // check if script tag exists and is already loaded
    if (document.getElementById('Freshchat-js-sdk')) {
      resolve();
      return;
    }

    const freshChatScript = document.createElement('script');
    freshChatScript.id = 'Freshchat-js-sdk';
    freshChatScript.async = true;
    freshChatScript.src = 'https://disqo-assist.freshchat.com/js/widget.js';

    freshChatScript.onload = () => resolve();
    freshChatScript.onerror = (e) => reject(e);

    document.head.appendChild(freshChatScript);
  });

  return scriptLoadingPromise;
};

/**
 * Open FreshChat widget
 */
export const openFreshchat = () => {
  if (window.fcWidget) {
    window.fcWidget.open();
  }
};

/**
 * Initialize FreshChat
 */
export const initFreshChat = (
  pointsData        : ACTUserPointsResponse | null | undefined,
  userInfo          : IDPUserResponseInternal | null | undefined,
  verificationStatus: VERUserVerificationStatus | null | undefined,
  earnedPoints      : string,
  options?          : { hideDefaultLauncher?: boolean; onLauncherVisible?: () => void }
) => {
  const freshChatUuidKey      = process.env.FRESH_CHAT_UUID;
  const restoreId             = getLocalStorage(LOCAL_STORAGE_IDS.FRESHCHAT_RESTORE);
  const isMissingRequiredData = !pointsData || !userInfo || typeof pointsData !== 'object' || typeof userInfo !== 'object';

  // Check whether FreshChat should be initialized
  if (isMissingRequiredData || isInMobileApp()) {
    destroyFreshChat();
    return;
  }

  // Check if FreshChat is already initialized
  if (window.fcWidget && window.fcWidget.isInitialized()) {
    // If need to show the launcher (hideDefaultLauncher is false/undefined)
    if (!options?.hideDefaultLauncher) {
        window.fcWidget.show();
        options?.onLauncherVisible?.();
    }
  }

  const fcUserInfo = {
    externalId        : userInfo.id,
    current_points    : pointsData.currentPoints,
    lifetime_points   : pointsData.lifetimePoints,
    earned_this_year  : earnedPoints,
    email             : userInfo.email,
    customer_name     : userInfo.firstName,
    customer_last_name: userInfo.lastName,
    VVS               : verificationStatus?.verificationStatus
  };

  let fcInitInfo: FcInitInfoType = {
    token     : process.env.FRESH_CHAT_TOKEN,
    host      : process.env.FRESH_CHAT_HOST,
    restoreId : restoreId ? restoreId : null,
    fullscreen: isMobile(),
    firstName : userInfo.firstName,
    lastName  : userInfo.lastName,
    hideChatButton: options?.hideDefaultLauncher,
    ...fcUserInfo
  };

  if (freshChatUuidKey) {
    fcInitInfo = {...fcInitInfo, widgetUuid: freshChatUuidKey};
  }

  const performInit = () => {
    if (!window.fcWidget) {
      return;
    }

    try {
      if (window.fcWidget.isInitialized()) {
        window.fcWidget.user.update({
          meta: {
            ...fcUserInfo
          }
        });
      } else {
        window.fcWidget.init({
          ...fcInitInfo
        });

        // Global class toggle for open state
        // This class is used in global CSS to disable the custom mobile shrinking styles when the widget is open
        window.fcWidget.on('widget:opened', () => {
          document.body.classList.add('fc-widget-open');
        });
        window.fcWidget.on('widget:closed', () => {
          document.body.classList.remove('fc-widget-open');
        });
      }

      // handle first open to show default launcher subsequently
      if (options?.hideDefaultLauncher) {
        const onOpened = () => {
          // Notify that launcher logic is active (hides custom button)
          // i dont show the default launcher(sdk button) yet, wait for closed event to show it
          options?.onLauncherVisible?.();
        };

        const onClosed = () => {
           //ensures the default launcher is shown after the widget is closed
            if (window.fcWidget) {
                window.fcWidget.init({
                    ...fcInitInfo,
                    hideChatButton: false
                });
            }
        };

        window.fcWidget.on("widget:opened", onOpened);
        window.fcWidget.on("widget:closed", onClosed);
      } else {
          options?.onLauncherVisible?.();
      }

      window.fcWidget.on("message:sent", () => {
        analyticsTrackChatBotStart();
      });

      window.fcWidget.user.get((resp: { status: number; data: FreshChatResponseType; }) => {
        const status = resp && resp.status;
        if (status !== 200) {
          window.fcWidget.user.setProperties({
            ...fcUserInfo
          });
          window.fcWidget.on('user:created', (resp: { status: number; data: FreshChatResponseType; }) => {
            const status = resp && resp.status;
            const data = resp && resp.data;
            if (status === 200 && data.restoreId) {
              setLocalStorage(LOCAL_STORAGE_IDS.FRESHCHAT_RESTORE, data.restoreId, false);
            }
          });
        }
      });
    } catch (e) {
      logError('initFreshChat', e);
      return;
    }
  };

  performInit();
};
